import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useUsersApi } from '../hooks/useUsersApi';
import { UserFormDialog } from './users/UserFormDialog';
import { UserDeleteDialog } from './users/UserDeleteDialog';
import type { UserData } from '../types/user';

export function UsersPage() {
    const { users, loading, error, validationErrors, createUser, updateUser, deleteUser } = useUsersApi();
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [deletingUser, setDeletingUser] = useState<UserData | null>(null);

    const handleCreate = () => {
        setEditingUser(null);
        setFormOpen(true);
    };

    const handleEdit = (user: UserData) => {
        setEditingUser(user);
        setFormOpen(true);
    };

    const handleDeleteClick = (user: UserData) => {
        setDeletingUser(user);
        setDeleteOpen(true);
    };

    const handleSave = async (data: Record<string, unknown>) => {
        if (editingUser) {
            await updateUser(editingUser.id, data);
        } else {
            await createUser(data);
        }
        setFormOpen(false);
    };

    const handleDeleteConfirm = async (id: number) => {
        await deleteUser(id);
    };

    if (error) {
        return (
            <div className="p-6 text-center">
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-xl font-semibold text-gray-900">Utilisateurs</h1>
                <Button onClick={handleCreate}>Nouvel utilisateur</Button>
            </div>

            {loading ? (
                <div className="text-center text-gray-500 py-8">Chargement...</div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sociétés</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                                    <td className="px-4 py-3">
                                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                                            {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {user.connectors.map((c) => c.company_name).join(', ')}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-2">
                                        <Button variant="ghost" size="sm" onClick={() => handleEdit(user)}>
                                            Modifier
                                        </Button>
                                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteClick(user)}>
                                            Supprimer
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                                        Aucun utilisateur trouvé.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <UserFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                user={editingUser}
                validationErrors={validationErrors}
                onSave={handleSave}
            />

            <UserDeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                user={deletingUser}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
}
